import { useCallback, useEffect, useState } from "react";
import axios from "axios";
import { toast } from "sonner";
import { Gift, HandHeart, Sun, Trash2, Printer, Lock, Plus } from "lucide-react";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const KINDS = [
  { key: "oferta", label: "Ofereço", icon: Gift, hint: "sacrifícios, trabalho, orações oferecidas" },
  { key: "pedido", label: "Peço", icon: HandHeart, hint: "petições e necessidades" },
  { key: "agradecimento", label: "Agradeço", icon: Sun, hint: "ações de graças" },
];
const KIND_STYLE = {
  oferta: "bg-[#FBF6E8] text-[#8a6d1d] border-[#E3D5A8]",
  pedido: "bg-[#EEF4F2] text-[#0E4D45] border-[#CBD9D4]",
  agradecimento: "bg-[#FDF0E6] text-[#9A5B1F] border-[#EBD2B8]",
};
const KIND_LABEL = { oferta: "Ofereço", pedido: "Peço", agradecimento: "Agradeço" };

export const CapitalContributions = () => {
  const [items, setItems] = useState([]);
  const [burnLabel, setBurnLabel] = useState("");
  const [kind, setKind] = useState("oferta");
  const [text, setText] = useState("");
  const [saving, setSaving] = useState(false);

  const load = useCallback(async () => {
    try {
      const r = await axios.get(`${API}/members/capital`, { withCredentials: true });
      setItems(r.data?.items || []);
      setBurnLabel(r.data?.burn_label || "");
    } catch (e) {
      console.error("[Capital] load:", e?.message);
    }
  }, []);

  useEffect(() => { load(); }, [load]);

  const add = async () => {
    if (text.trim().length < 2) { toast.error("Escreva a sua contribuição."); return; }
    setSaving(true);
    try {
      await axios.post(`${API}/members/capital`, { kind, text: text.trim() }, { withCredentials: true });
      setText("");
      toast.success("Contribuição registada no seu Capital de Graças.");
      load();
    } catch (e) {
      console.error("[Capital] create:", e?.message);
      toast.error("Não foi possível registar.");
    }
    setSaving(false);
  };

  const remove = async (it) => {
    try {
      await axios.delete(`${API}/members/capital/${it.id}`, { withCredentials: true });
      setItems((prev) => prev.filter((x) => x.id !== it.id));
    } catch { toast.error("Erro a remover."); }
  };

  const printPdf = async () => {
    try {
      const r = await axios.get(`${API}/members/capital/pdf`, {
        withCredentials: true, responseType: "blob",
      });
      const url = window.URL.createObjectURL(new Blob([r.data], { type: "application/pdf" }));
      const a = document.createElement("a");
      a.href = url;
      a.download = "capital-de-gracas.pdf";
      document.body.appendChild(a);
      a.click();
      a.remove();
      window.URL.revokeObjectURL(url);
    } catch (e) {
      toast.error(e?.response?.status === 404
        ? "Ainda não tem contribuições neste período."
        : "Erro a gerar o PDF.");
    }
  };

  return (
    <div className="mt-8 bg-white border border-[hsl(var(--border))] p-6 max-w-2xl" data-testid="capital-private-section">
      <div className="flex items-start justify-between gap-4">
        <div>
          <div className="flex items-center gap-2">
            <Lock className="h-4 w-4 text-[#B08D1E]" />
            <span className="text-xs uppercase tracking-[0.18em] text-[hsl(var(--muted-foreground))]">
              O meu Capital de Graças · confidencial
            </span>
          </div>
          <p className="mt-2 text-xs text-[hsl(var(--muted-foreground))] leading-relaxed">
            Só você vê estas contribuições. Imprima-as e entregue-as no Santuário,
            onde serão queimadas a <b>{burnLabel || "18 de cada mês"}</b>.
          </p>
        </div>
        <button
          data-testid="capital-print-btn"
          onClick={printPdf}
          className="inline-flex items-center gap-2 px-4 py-2 border border-[hsl(var(--primary))] text-[hsl(var(--primary))] text-xs uppercase tracking-[0.18em] font-semibold hover:bg-[hsl(var(--primary))] hover:text-white transition-colors shrink-0"
        >
          <Printer className="h-4 w-4" /> Imprimir
        </button>
      </div>

      <div className="mt-5 flex gap-2">
        {KINDS.map((k) => {
          const Icon = k.icon;
          const active = kind === k.key;
          return (
            <button
              key={k.key}
              data-testid={`capital-kind-${k.key}`}
              onClick={() => setKind(k.key)}
              title={k.hint}
              className={`flex-1 inline-flex items-center justify-center gap-1.5 px-2 py-2 border text-xs uppercase tracking-wider transition-colors ${
                active
                  ? "border-[hsl(var(--primary))] bg-[hsl(var(--primary))] text-white"
                  : "border-[hsl(var(--border))] text-[hsl(var(--muted-foreground))] hover:border-[hsl(var(--primary))]"
              }`}
            >
              <Icon className="h-3.5 w-3.5" /> {k.label}
            </button>
          );
        })}
      </div>

      <div className="mt-3 flex gap-2">
        <input
          data-testid="capital-text-input"
          value={text}
          onChange={(e) => setText(e.target.value)}
          onKeyDown={(e) => { if (e.key === "Enter") add(); }}
          maxLength={600}
          placeholder={`${KIND_LABEL[kind]}…`}
          className="flex-1 border border-[hsl(var(--border))] px-3 py-2 text-sm outline-none focus:border-[hsl(var(--primary))] bg-[hsl(var(--background))]"
        />
        <button
          data-testid="capital-add-btn"
          onClick={add}
          disabled={saving || text.trim().length < 2}
          className="inline-flex items-center gap-1.5 px-4 py-2 bg-[hsl(var(--primary))] text-white text-xs uppercase tracking-[0.18em] font-semibold disabled:opacity-40"
        >
          <Plus className="h-4 w-4" /> Registar
        </button>
      </div>

      {items.length > 0 && (
        <ul className="mt-5 space-y-2" data-testid="capital-list">
          {items.map((it) => (
            <li key={it.id} className="flex items-start gap-3 text-sm border-b border-[hsl(var(--border))]/60 pb-2"
              data-testid={`capital-item-${it.id}`}>
              <span className={`text-[10px] uppercase tracking-wider px-2 py-0.5 border shrink-0 mt-0.5 ${KIND_STYLE[it.kind]}`}>
                {KIND_LABEL[it.kind]}
              </span>
              <span className="flex-1 text-[hsl(var(--foreground))] leading-relaxed">{it.text}</span>
              <button onClick={() => remove(it)} title="Remover" data-testid={`capital-delete-${it.id}`}
                className="p-1 text-[hsl(var(--muted-foreground))] hover:text-red-600 shrink-0">
                <Trash2 className="h-3.5 w-3.5" />
              </button>
            </li>
          ))}
        </ul>
      )}
    </div>
  );
};

export default CapitalContributions;
